import { useEffect, useRef } from "react";
import { Switch, Route, Router as WouterRouter, Redirect, useLocation } from "wouter";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { ClerkProvider, SignIn, SignUp, Show, useClerk, useAuth } from "@clerk/react";
import { shadcn } from "@clerk/themes";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import Dashboard from "@/pages/dashboard";
import Today from "@/pages/today";
import History from "@/pages/history";
import { useRunnerView } from "@/hooks/useRunnerView";
import Squads from "@/pages/squads";
import SquadDetail from "@/pages/squad-detail";
import Checkin from "@/pages/checkin";
import Members from "@/pages/members";
import MemberDetail from "@/pages/member-detail";
import DeleteAccount from "@/pages/delete-account";
import Layout from "@/components/layout";
import ProfileSetupGuard from "@/components/ProfileSetupGuard";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { RegionPage, RegionUnavailable } from "@/pages/region";
import { signUpRegionState } from "@/lib/region";
import { useGetRegion } from "@workspace/api-client-react";
import FinishingSetup from "@/pages/finishing-setup";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30,
      retry: 1,
    },
  },
});

// Use the build-time key directly. publishableKeyFromHost derived a pk_live
// from the page hostname (clerk.<host>), a Replit-managed-Clerk convention;
// on Render our production frontend API is clerk.peakrunning.com, which the
// hostname app.peakrunning.com does not derive to.
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

const clerkAppearance = {
  theme: shadcn,
  cssLayerName: "clerk",
  options: {
    logoPlacement: "inside" as const,
    logoLinkUrl: basePath || "/",
    logoImageUrl: `${window.location.origin}${basePath}/logo-stacked.svg`,
  },
  variables: {
    colorPrimary: "#C9F527",
    colorForeground: "#0a0a0a",
    colorMutedForeground: "#737373",
    colorDanger: "#ef4444",
    colorBackground: "#ffffff",
    colorInput: "#f4f4f5",
    colorInputForeground: "#0a0a0a",
    colorNeutral: "#e4e4e7",
    fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif",
    borderRadius: "0.25rem",
  },
  elements: {
    rootBox: "w-full",
    cardBox: "bg-white rounded border border-zinc-200 w-[440px] max-w-full overflow-hidden shadow-md",
    card: "!shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
    headerTitle: "text-foreground font-bold",
    headerSubtitle: "text-zinc-500",
    socialButtonsBlockButtonText: "text-zinc-700",
    formFieldLabel: "text-zinc-700 font-medium",
    footerActionLink: "text-zinc-900 font-semibold hover:text-zinc-700",
    footerActionText: "text-zinc-500",
    dividerText: "text-zinc-400",
    identityPreviewEditButton: "text-zinc-700",
    formFieldSuccessText: "text-green-600",
    alertText: "text-zinc-800",
    // Clerk pins .cl-logoBox to a fixed 32px height, so a taller logo overflows
    // the box and butts straight into the header title. Force the box to size
    // to its content instead, then space it off the title.
    logoBox: "flex items-center justify-center !h-auto pt-3 pb-2",
    logoImage: "h-20 w-auto",
    socialButtonsBlockButton: "border border-zinc-200 hover:bg-zinc-50",
    formButtonPrimary: "bg-[#C9F527] text-zinc-900 font-semibold hover:opacity-90 shadow-none",
    formFieldInput: "border border-zinc-200 bg-zinc-50 text-zinc-900",
    footerAction: "border-t border-zinc-100",
    dividerLine: "bg-zinc-200",
    alert: "border border-zinc-200",
    otpCodeFieldInput: "border border-zinc-200",
    formFieldRow: "",
    main: "",
  },
};

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Show when="signed-in">
        <ProfileSetupGuard>{children}</ProfileSetupGuard>
      </Show>
      <Show when="signed-out">
        <Redirect to="/" />
      </Show>
    </>
  );
}

// WI#23 (coverage-map D6): a runner's home is Today, a coach's is the Dashboard. Sign-in and
// sign-up still land on /dashboard, so every existing redirect keeps working.
function DashboardOrToday() {
  const { isLoading, isRunnerView } = useRunnerView();
  if (isLoading) return null;
  return isRunnerView ? <Redirect to="/today" /> : <Dashboard />;
}

function HomeRedirect() {
  return (
    <>
      <Show when="signed-in">
        <Redirect to="/dashboard" />
      </Show>
      <Show when="signed-out">
        <Redirect to="/sign-in" />
      </Show>
    </>
  );
}

// WI#25 — when the post-purchase automatic sign-in can't finish, the server lands the buyer here
// with ?handoff=<reason>. Tell them what happened and that their purchase is safe.
const HANDOFF_MESSAGES: Record<string, string> = {
  expired: "That sign-in link has already been used or has expired. Sign in with the email you used at checkout.",
  failed: "We couldn't sign you in automatically. Sign in with the email you used at checkout. Your purchase is safe.",
  missing: "We couldn't sign you in automatically. Sign in with the email you used at checkout. Your purchase is safe.",
};

// WI#34 — where a completed account deletion lands, after the session is ended.
const ACCOUNT_MESSAGES: Record<string, string> = {
  deleted: "Account deleted.",
};

function SignInPage() {
  const handoff = new URLSearchParams(window.location.search).get("handoff");
  const account = new URLSearchParams(window.location.search).get("account");
  const handoffMessage = handoff
    ? HANDOFF_MESSAGES[handoff]
    : account
      ? ACCOUNT_MESSAGES[account]
      : undefined;
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-[480px]">
        {handoffMessage && (
          <p role="status" className="mb-4 rounded border border-border bg-accent/40 px-4 py-3 text-sm text-foreground">
            {handoffMessage}
          </p>
        )}
        <SignIn
          routing="path"
          path={`${basePath}/sign-in`}
          signUpUrl={`${basePath}/sign-up`}
          fallbackRedirectUrl={`${basePath}/dashboard`}
        />
      </div>
    </div>
  );
}

// WI#28 — the post-purchase hand-off lands here as /auth/complete#ticket=<Clerk sign-in ticket>.
// The ticket travels in the fragment so it never reaches the server or Render's request log. Read
// it once, wipe it from the address bar before anything can record it, then finish the sign-in
// with Clerk's ticket strategy. Failures land on the normal sign-in page and are reported.
function AuthCompletePage() {
  const clerk = useClerk();
  const { isLoaded, isSignedIn } = useAuth();
  const [, setLocation] = useLocation();
  const ticketRef = useRef<string | null>(null);
  if (ticketRef.current === null) {
    ticketRef.current = new URLSearchParams(window.location.hash.slice(1)).get("ticket") ?? "";
    window.history.replaceState(null, "", window.location.pathname);
  }
  const startedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded || startedRef.current) return;
    startedRef.current = true;
    if (isSignedIn) {
      setLocation("/dashboard");
      return;
    }
    const ticket = ticketRef.current;
    if (!ticket) {
      Sentry.captureMessage("Post-purchase sign-in reached /auth/complete without a ticket", { level: "error" });
      setLocation("/sign-in?handoff=missing");
      return;
    }
    (async () => {
      try {
        const signIn = await clerk.client.signIn.create({ strategy: "ticket", ticket });
        if (signIn.status !== "complete" || !signIn.createdSessionId) {
          throw new Error(`Ticket sign-in ended with status ${signIn.status ?? "none"}`);
        }
        await clerk.setActive({ session: signIn.createdSessionId });
        setLocation("/dashboard");
      } catch (err) {
        Sentry.captureException(err, { tags: { flow: "post-purchase-handoff" } });
        setLocation("/sign-in?handoff=failed");
      }
    })();
  }, [isLoaded, isSignedIn, clerk, setLocation]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-muted-foreground font-mono text-sm animate-pulse">Signing you in...</div>
    </div>
  );
}

// WI#43 — new sign-ups are US/Canada only. The server decides from the request's location (and
// refuses a new profile anyway if this page were bypassed); outside the region, the visitor gets
// the region page instead of a sign-up form. A location that couldn't be confirmed and a check that
// failed outright are separate recoverable states, both with Try again (#44, Kayla J4).
// test_country is QA-only and ignored by the server in production.
function SignUpPage() {
  const testCountry = new URLSearchParams(window.location.search).get("test_country") ?? undefined;
  const region = useGetRegion(testCountry ? { test_country: testCountry } : undefined);
  if (region.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading...</div>
      </div>
    );
  }
  const state = signUpRegionState(region);
  if (state === "check_failed" || state === "lookup_failed" || state === "origin_untrusted") {
    return <RegionUnavailable reason={state} error={region.error} onRetry={() => region.refetch()} />;
  }
  if (state === "outside_region") return <RegionUnavailable reason="outside_region" />;
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-[480px]">
        <SignUp
          routing="path"
          path={`${basePath}/sign-up`}
          signInUrl={`${basePath}/sign-in`}
          fallbackRedirectUrl={`${basePath}/dashboard`}
        />
      </div>
    </div>
  );
}

function JoinRedirect() {
  const { isSignedIn, isLoaded } = useAuth();
  const [, setLocation] = useLocation();
  const code = new URLSearchParams(window.location.search).get("code") ?? "";

  useEffect(() => {
    if (!isLoaded) return;
    if (code) {
      sessionStorage.setItem("peakrunning_invite_code", code.toUpperCase());
    }
    if (isSignedIn) {
      setLocation(code ? `/squads?code=${encodeURIComponent(code.toUpperCase())}` : "/squads");
    } else {
      setLocation("/sign-up");
    }
  }, [isLoaded, isSignedIn, code, setLocation]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading...</div>
    </div>
  );
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const qc = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId) {
        qc.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, qc]);

  return null;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={HomeRedirect} />
      <Route path="/sign-in/*?" component={SignInPage} />
      <Route path="/sign-up/*?" component={SignUpPage} />
      <Route path="/auth/complete" component={AuthCompletePage} />
      <Route path="/join" component={JoinRedirect} />
      <Route path="/region" component={RegionPage} />
      <Route path="/dashboard">
        <ProtectedRoute>
          <Layout>
            <RouteErrorBoundary routeName="Dashboard">
              <DashboardOrToday />
            </RouteErrorBoundary>
          </Layout>
        </ProtectedRoute>
      </Route>
      {/* #81 F2 — where the Free journey returns. Outside ProfileSetupGuard on purpose: the runner has
          no profile yet; this page replays their first action once the subscription attaches. */}
      <Route path="/account/setting-up">
        <Show when="signed-in">
          <RouteErrorBoundary routeName="Finishing setup">
            <FinishingSetup />
          </RouteErrorBoundary>
        </Show>
        <Show when="signed-out">
          <Redirect to="/sign-in" />
        </Show>
      </Route>
      {/* WI#34 — outside ProfileSetupGuard on purpose: an account with no runner profile yet must
          still be able to delete itself. */}
      <Route path="/account/delete">
        <Show when="signed-in">
          <Layout>
            <RouteErrorBoundary routeName="Delete account">
              <DeleteAccount />
            </RouteErrorBoundary>
          </Layout>
        </Show>
        <Show when="signed-out">
          <Redirect to="/" />
        </Show>
      </Route>
      <Route path="/history">
        <ProtectedRoute>
          <Layout>
            <RouteErrorBoundary routeName="My History">
              <History />
            </RouteErrorBoundary>
          </Layout>
        </ProtectedRoute>
      </Route>
      <Route path="/today">
        <ProtectedRoute>
          <Layout>
            <RouteErrorBoundary routeName="Today">
              <Today />
            </RouteErrorBoundary>
          </Layout>
        </ProtectedRoute>
      </Route>
      <Route path="/squads">
        <ProtectedRoute>
          <Layout>
            <RouteErrorBoundary routeName="Squads">
              <Squads />
            </RouteErrorBoundary>
          </Layout>
        </ProtectedRoute>
      </Route>
      <Route path="/squads/:id/checkin">
        {(params) => (
          <ProtectedRoute>
            <Layout>
              <RouteErrorBoundary routeName="Checkin">
                <Checkin squadId={Number(params.id)} />
              </RouteErrorBoundary>
            </Layout>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/squads/:id">
        {(params) => (
          <ProtectedRoute>
            <Layout>
              <RouteErrorBoundary routeName="Squad Detail">
                <SquadDetail squadId={Number(params.id)} />
              </RouteErrorBoundary>
            </Layout>
          </ProtectedRoute>
        )}
      </Route>
      <Route path="/members">
        <ProtectedRoute>
          <Layout>
            <RouteErrorBoundary routeName="Members">
              <Members />
            </RouteErrorBoundary>
          </Layout>
        </ProtectedRoute>
      </Route>
      <Route path="/members/:id">
        {(params) => (
          <ProtectedRoute>
            <Layout>
              <RouteErrorBoundary routeName="Member Detail">
                <MemberDetail memberId={Number(params.id)} />
              </RouteErrorBoundary>
            </Layout>
          </ProtectedRoute>
        )}
      </Route>
      <Route>
        <ProtectedRoute>
          <NotFound />
        </ProtectedRoute>
      </Route>
    </Switch>
  );
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      signInFallbackRedirectUrl={`${basePath}/dashboard`}
      signUpFallbackRedirectUrl={`${basePath}/dashboard`}
      afterSignOutUrl={`${basePath}/`}
      localization={{
        signIn: {
          start: {
            title: "Welcome back",
            subtitle: "Sign in to your PeakRunning account",
          },
        },
        signUp: {
          start: {
            title: "Join PeakRunning",
            subtitle: "Create your account to get started",
          },
        },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkQueryClientCacheInvalidator />
        <TooltipProvider>
          <Router />
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  if (!clerkPubKey) {
    return (
      <div className="flex items-center justify-center min-h-screen text-sm text-muted-foreground">
        Missing VITE_CLERK_PUBLISHABLE_KEY
      </div>
    );
  }

  return (
    <WouterRouter base={basePath}>
      <ClerkProviderWithRoutes />
    </WouterRouter>
  );
}

export default App;
